import React, { useEffect, useState } from 'react';
import { useParams } from "react-router-dom";
import axios from "axios";
import {Avatar, Button, Descriptions, Image, Spin} from "antd";
import {fetchRevisits, fetchVisits} from "../services/visitService";
import {getDispensaryDataPatient} from "../services/dispansery";
import {ClipboardDocumentCheckIcon} from "@heroicons/react/24/solid";
import {BsPrinterFill} from "react-icons/bs";
import axiosInstance from '../axios/axiosInstance';
import { PageCard, PageShell } from '../components/ui';
import { useTranslation } from 'react-i18next';

const ParentAdmission = () => {
    const { t } = useTranslation();
    const { index } = useParams();
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mostRecentVisit, setMostRecentVisit] = useState(null);
    const [visits, setVisits] = useState({});
    const [visitId, setVisitId] = useState(null);
    const [dataCache, setDataCache] = useState({});
    const [revisits, setRevisits] = useState([]);

    const [dispensaryData, setDispensaryData] = useState(null);

    const printPage = () => {
        window.print();
    }

    useEffect(() => {
        const fetchDispensaryData = async () => {
            if (mostRecentVisit && mostRecentVisit.id) {
                try {
                    const data = await getDispensaryDataPatient(mostRecentVisit.id);
                    setDispensaryData(data);
                } catch (error) {
                    console.error('Ошибка при получении данных о диспансере:', error);
                }
            }
        };
        fetchDispensaryData();
    }, [mostRecentVisit]);

    const renderDispensaryDates = () => {
        if (dispensaryData && dispensaryData.data) {
            const currentVisitDate = dispensaryData.data[0]?.visit?.date;
            const mouthDays = dispensaryData.data.map(item => item.mouth_days).flat();
            return (
                <ul>
                    {mouthDays.length > 0
                        ? mouthDays.map(date => <li key={date}>{date}</li>)
                        : <li>{t('admissionPrint.noDispensary')}</li>}
                </ul>
            );
        }
        return <li>{t('admissionPrint.noDispensary')}</li>;
    };

    useEffect(() => {
        if (visits && Object.keys(visits).length > 0) {
            const patientVisits = visits[index];
            if (Array.isArray(patientVisits)) {
                const recentVisit = patientVisits.reduce((latest, visit) => {
                    if (!visit.parent_id && ["queue", "examined", "new"].includes(visit.status)) {
                        if (!latest || new Date(visit.date_at) > new Date(latest.date_at)) {
                            return visit;
                        }
                    }
                    return latest;
                }, null);
                setMostRecentVisit(recentVisit);
            }
        }
    }, [visits, index]);


    const fetchPatientVisits = async () => {
        try {
            let visitData = [];
            if (!dataCache[index]) {
                const fetchAllPages = async (patientId) => {
                    let allData = [];
                    let page = 1;
                    let lastPage = 1;

                    while (page <= lastPage) {
                        try {
                            const response = await fetchVisits(patientId, page);
                            const pageData = response.data.data;
                            const meta = response.data.meta;

                            allData = [...allData, ...pageData];
                            lastPage = meta.last_page; // обновляем количество страниц
                            page += 1; // переходим к следующей странице
                        } catch (error) {
                            console.error(`Ошибка при получении данных со страницы ${page}:`, error);
                            break;
                        }
                    }

                    return allData;
                };

                visitData = await fetchAllPages(index);

                setDataCache(prevCache => ({
                    ...prevCache,
                    [index]: visitData,
                }));
            } else {
                visitData = dataCache[index];
            }

            const recentVisit = visitData.find(visit =>
                visit.parent_id === null &&
                (visit.bill === "payed" || visit.bill === "pending") && (visit.status === "new" || visit.status === "examined" || visit.status === "queue")
            );

            if (recentVisit) {
                setVisitId(recentVisit.id);
            } else {
                setVisitId(null);
            }

            setVisits(prevVisits => ({
                ...prevVisits,
                [index]: visitData,
            }));
        } catch (error) {
            console.error('Ошибка при получении данных о визитах:', error);
        }
    };


    const fetchPatientRevisits = async () => {
        try {
            const page = 1; // Assuming you want the first page, you can modify this if needed
            const response = await fetchRevisits(index, page);
            setRevisits(response.data.data);
        } catch (error) {
            console.error('Ошибка при получении данных о повторных визитах:', error);
        }
    };

    useEffect(() => {
        fetchPatientVisits();
        fetchPatientRevisits();
    }, [index, dataCache]);

    useEffect(() => {
        const fetchPatient = async () => {
            try {
                const response = await axiosInstance.get(`/patients/${index}`);
                setPatient(response.data.data);
                setLoading(false);
            } catch (error) {
                setError(t('admissionPrint.loadError'));
                setLoading(false);
            }
        };
        fetchPatient();
    }, [index]);

    if (loading) {
        return (
            <PageShell>
                <div className="flex min-h-[40vh] items-center justify-center">
                    <Spin spinning tip={t('admissionPrint.loading')} size="large" />
                </div>
            </PageShell>
        );
    }

    if (error) {
        return (
            <PageShell>
                <PageCard className="p-4 text-sm text-red-600">{error}</PageCard>
            </PageShell>
        );
    }

    return (
        <PageShell className="print-area">
            <PageCard className="p-3 sm:p-5">
            {patient ? (
              <>
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2 print-button">
                      <h1 className="m-0 text-sm font-semibold text-slate-800 sm:text-[15px]">{t('admissionPrint.title')}</h1>
                      <Button onClick={printPage} icon={<BsPrinterFill className="h-4 w-4" />} size="middle">
                          {t('admissionPrint.print')}
                      </Button>
                  </div>
                  <Descriptions
                      bordered
                      size="small"
                      column={{
                          xs: 1,
                          sm: 1,
                          md: 2,
                          lg: 2,
                          xl: 2,
                          xxl: 2,
                      }}
                  >
                      <Descriptions.Item label={t('admissionPrint.photo')}>
                          <Avatar size={64} loading="lazy" src={`${patient.avatar}`} alt="avatar" variant="rounded" />
                      </Descriptions.Item>
                      <Descriptions.Item label={t('admissionPrint.code')}>SHH7FX6DG</Descriptions.Item>
                      <Descriptions.Item label={t('common.name')}>{patient.name}</Descriptions.Item>
                      <Descriptions.Item label={t('patientCard.birthDate')}>{patient.birth_at}</Descriptions.Item>

                  </Descriptions>
                  <Descriptions
                      className="mt-4"
                      title={t('admissionPrint.diseases')}
                      bordered
                      column={{
                          xs: 1,
                          sm: 1,
                          md: 1,
                          lg: 1,
                          xl: 1,
                          xxl: 1,
                      }}
                  >
                      <Descriptions.Item labelStyle={{minWidth: 96, maxWidth: 120}} label={t('admissionPrint.mkb10')}>

                          {patient.mkb10.map(item => (
                              <div className="w-full" key={item.id}>
                                  <p>{item.name}</p>
                              </div>
                          ))}
                      </Descriptions.Item>
                  </Descriptions>
                  <Descriptions
                      className="mt-4"
                      title={t('admissionPrint.conclusion')}
                      bordered
                      column={{
                          xs: 1,
                          sm: 1,
                          md: 1,
                          lg: 1,
                          xl: 1,
                          xxl: 1,
                      }}
                  >
                      <Descriptions.Item labelStyle={{minWidth: 112, maxWidth: 200}} label={t('admissionPrint.conclusionLabel')}>

                          {mostRecentVisit && mostRecentVisit.remark ? '' : t('admissionPrint.noConclusion')}
                      </Descriptions.Item>
                  </Descriptions>
                  <Descriptions
                      className="mt-4"
                      title={t('admissionPrint.revisits')}
                      bordered
                      column={{
                          xs: 1,
                          sm: 1,
                          md: 1,
                          lg: 1,
                          xl: 1,
                          xxl: 1,
                      }}
                  >
                      <Descriptions.Item labelStyle={{minWidth: 112, maxWidth: 200}} label={t('admissionPrint.revisits')}>
                          {revisits.length > 0 ? (
                              <ul>
                                  {revisits.map(revisit => (
                                      <li key={revisit.id}>{revisit.date_at}</li>
                                  ))}
                              </ul>
                          ) : (
                              <p>{t('admissionPrint.noRevisits')}</p>
                          )}
                      </Descriptions.Item>
                  </Descriptions>
                  <Descriptions
                      className="mt-4"
                      title={t('admissionPrint.dispensary')}
                      bordered
                      column={{
                          xs: 1,
                          sm: 1,
                          md: 1,
                          lg: 1,
                          xl: 1,
                          xxl: 1,
                      }}
                  >
                      <Descriptions.Item labelStyle={{minWidth: 112, maxWidth: 200}} label={t('admissionPrint.dispensaryDate')}>

                          {renderDispensaryDates()}
                      </Descriptions.Item>
                  </Descriptions>
                  <Descriptions
                      className="mt-4"
                      title={t('admissionPrint.files')}
                      bordered
                      column={{
                          xs: 1,
                          sm: 1,
                          md: 1,
                          lg: 1,
                          xl: 1,
                          xxl: 1,
                      }}
                  >
                      <Descriptions.Item labelStyle={{minWidth: 112, maxWidth: 200}} label={t('admissionPrint.filesTypes')}>
                          <Image.PreviewGroup
                              preview={{
                                  onChange: (current, prev) => console.log(`current index: ${current}, prev index: ${prev}`),
                              }}
                          >
                              {mostRecentVisit ? mostRecentVisit.files.map(file => (
                                  <Image key={file.id}
                                         width={100}
                                         src={file.url}
                                  />
                              )) : ''}
                          </Image.PreviewGroup>
                      </Descriptions.Item>

                  </Descriptions>

              </>
            ) : (
                <p className="m-0 text-sm text-slate-500">{t('admissionPrint.notFound')}</p>
            )}
            </PageCard>
        </PageShell>
    );
};



export default ParentAdmission;
